import { shouldShowPowerDistributionSection } from './facilityPowerScopeSections';
import { resolveEnabledStepIds } from './resolveRequestFormSteps';
import {
  isOptionalRequestFormStepId,
  LEGACY_OPTIONAL_REQUEST_FORM_STEP_IDS,
  OPTIONAL_REQUEST_FORM_STEP_IDS,
  type OptionalRequestFormStepId,
} from '../config/requestFormStepRegistry';

const POWER_ADDITIONAL_INFORMATION_FIELD = 'powerAdditionalInformation';

/** Snake_case DB columns cleared per step — mirror server/config/requestStepClearColumns.ts */
const REQUEST_STEP_CLEAR_COLUMN_KEYS: Record<
  OptionalRequestFormStepId,
  readonly string[]
> = {
  'site-information': [
    'site_room_length_ft_in',
    'site_room_width_sqft_sqm',
    // NOTE: 'site_additional_site_information' is Opportunity "Additional Information"
    // (formSection 'Opportunity Information'), NOT a Site field — removing Site Information
    // scope must NOT clear it. The Site Dimensions notes field is
    // 'site_additional_size_information' (below). (#1148, Kiran #17)
    'site_data_center_flooring',
    'site_raised_floor_height',
    'site_ceiling_height',
    'site_weight_support_of_floor',
    'site_dropped_ceiling_height',
    'site_plenum_height',
    'site_slab_to_dropped_ceiling_height',
    'site_additional_size_information',
  ],
  'facility-power': [
    'power_total_available_power_kw',
    'power_main_nominal_lv_voltage',
    'power_anticipated_final_mw_size',
    'power_total_data_center_it_load_kw',
    'power_average_it_load_per_rack_kw',
    'power_maximum_it_load_per_rack_kw',
    'power_number_and_type_input_per_rack',
    'power_additional_rack_power_info',
    'power_additional_information',
    'power_facility_description',
    'power_main_nominal_amps',
  ],
  'large-ups': [
    'power_existing_site_protected_by_large_kw_ups',
    'power_model_of_existing_ups',
    'power_level_of_redundancy',
    'power_required_redundancy_for_ups',
    'power_battery_runtime_required_min',
    'power_battery_chemistry',
    'power_source_location',
    'power_large_ups_description',
    'power_fire_suppression_needs',
    'power_types_of_ups',
    'power_input_power_phase',
    'power_runtime',
    'power_ac_or_dc_power',
  ],
  'power-distribution': [
    'power_distribution_voltage_to_load',
    'power_overhead_busway_or_under_floor',
    'power_outlet_switching_or_monitored',
    'power_busway_amperage',
    'power_busway_length_ft',
    'power_ups_output_voltage',
    'power_tap_of_box_requirements',
  ],
  'rack-power': [
    'rack_platform',
    'rack_quantity',
    'rack_dimensions_length',
    'rack_dimensions_height',
    'rack_dimensions_width',
    'rack_additional_rack_information',
    'rack_description',
    'rack_use',
    'rack_seismic_rated',
    'rack_max_load_rating',
    'rack_color',
    'rack_requires_liquid_to_chip',
    'rack_describe_elevations',
    'rack_pdu_mounting_type',
    'rack_pdu_count_per_rack',
    'rack_pdu_outlet_switching_monitored',
    'rack_pdu_outlet_types_quantity',
    'rack_pdu_power_type',
    'rack_pdu_max_kw_per_rack',
    'rack_pdu_additional_info',
    'rack_pdu_manufacturer',
    'rack_pdu_model',
    'rack_pdu_input_plug_type',
    'rack_pdu_dc_power_shelves',
    'rack_pdu_capacity_amps',
    'rack_pdu_capacity_volts',
    'rack_pdu_capacity_kw',
    'rack_pdu_load_readings_kw',
    'rack_pdu_location',
  ],
  'small-ups': [
    'power_small_ups_description',
    'power_small_ups_additional_info',
    'small_ups_manufacturer',
    'small_ups_model',
    'small_ups_serial_number',
    'small_ups_input_voltage',
    'small_ups_input_plug',
    'small_ups_capacity',
    'small_ups_load_percentage',
    'small_ups_battery_cabinet_qty',
    'small_ups_mounting',
    'small_ups_outlets',
    'small_ups_backup_generator',
  ],
  'cooling-readiness': [
    'cooling_total_available_capacity_kw',
    'cooling_required_redundancy_for_system',
    'cooling_is_existing_system_onsite',
    'cooling_is_chilled_water_available',
    'cooling_additional_readiness_info',
    'cooling_temp_rise_pref_through_it_equipment',
    'cooling_description',
    'cooling_describe_approach',
    'cooling_rear_door_hex_desired',
    'cooling_describe_rear_door_loop',
  ],
  'liquid-cooling': [
    'lc_project_requires_liquid_cooling',
    'lc_total_liquid_cooling_thermal_load_per_sfn',
    'lc_number_of_liquid_cooled_racks_to_be_served',
    'lc_number_of_air_cooled_racks',
    'lc_liquid_cooling_vs_peak_rack_it_load',
    'lc_total_it_load_per_data_hall',
    'lc_redundancy_strategy',
    'lc_ethylene_glycol_pct',
    'lc_tps',
    'lc_tpr',
    'lc_connection_type_primary',
    'lc_primary_flow_valve',
    'lc_coolant',
    'lc_tss',
    'lc_tsr',
    'lc_flowrate_requirement',
    'lc_filtration',
    'lc_maximum_pressure_rating',
    'lc_rack_pressure_drop',
    'lc_external_static_pressure_requirement',
    'lc_connection_type_secondary',
    'lc_additional_information_sfn',
    'lc_secondary_fluid_network',
    'lc_datahall_ceiling_height',
    'lc_datahall_ceiling_structural_support_rating',
    'lc_datahall_floor_type',
    'lc_datahall_raised_floor_depth',
    'lc_rack_arrangement_within_the_datahall',
    'lc_cdu_arrangement_within_the_datahall',
    'lc_cdu_redundancy_definition',
    'lc_cdu_primary_piping_connections',
    'lc_cdu_secondary_piping_connections',
    'lc_tcs_flow_pathway_definition',
    'lc_tcs_arrangement_type',
    'lc_tcs_routing_location',
    'lc_tcs_routing_special_requirements',
    'lc_tcs_rack_control_type',
    'lc_tcs_rack_connection_size',
    'lc_tcs_rack_connection_type',
    'lc_tcs_loop_pressure_maximum_rating',
    'lc_tcs_loop_temperature_maximum_rating',
    'lc_tcs_loop_pipe_diameter',
    'lc_tcs_coolant',
    'lc_required_tcs_coolant_flowrate_per_rack',
    'lc_additional_information_tcs',
    'lc_description',
    'lc_liquid_to_chip_desired',
    'lc_cdu_type',
    'lc_vertiv_supplies_row_manifolds',
    'lc_vertiv_supplies_rack_manifolds',
    'lc_cdu_redundancy',
    'lc_leak_detection_approach',
  ],
};

function snakeToCamel(column: string): string {
  return column.replace(/_([a-z0-9])/g, (_, letter: string) =>
    letter.toUpperCase(),
  );
}

const REQUEST_STEP_FORM_FIELD_KEYS: Record<
  OptionalRequestFormStepId,
  readonly string[]
> = Object.fromEntries(
  Object.entries(REQUEST_STEP_CLEAR_COLUMN_KEYS).map(([stepId, columns]) => [
    stepId,
    columns.map(snakeToCamel),
  ]),
) as Record<OptionalRequestFormStepId, readonly string[]>;

function normalizeStoredIds(stored: string[] | null | undefined): string[] {
  if (stored == null) {
    return [...LEGACY_OPTIONAL_REQUEST_FORM_STEP_IDS];
  }
  return stored;
}

/** Optional steps removed when scope changes from previous → next stored ids. */
export function diffRemovedOptionalStepIds(
  previousStored: string[] | null | undefined,
  nextStored: string[] | null | undefined,
): OptionalRequestFormStepId[] {
  const previous = new Set(normalizeStoredIds(previousStored));
  const next = new Set(normalizeStoredIds(nextStored));
  const removed: OptionalRequestFormStepId[] = [];

  for (const stepId of previous) {
    if (!next.has(stepId) && isOptionalRequestFormStepId(stepId)) {
      removed.push(stepId);
    }
  }

  return removed;
}

function shouldRetainPowerAdditionalInformation(
  nextEnabledStepIds: readonly string[],
  stage?: string | null,
): boolean {
  if (nextEnabledStepIds.includes('facility-power')) {
    return true;
  }
  if (nextEnabledStepIds.includes('large-ups')) {
    return true;
  }
  if (
    nextEnabledStepIds.includes('power-distribution') &&
    shouldShowPowerDistributionSection({ stage })
  ) {
    return true;
  }
  return false;
}

/** Mirror server/config/requestStepClearColumns.ts (#1330). */
export function expandRemovedStepIdsForDataClear(
  removedStepIds: readonly string[],
  nextStoredStepIds: string[] | null | undefined,
): OptionalRequestFormStepId[] {
  const nextSet = new Set(resolveEnabledStepIds(nextStoredStepIds));
  const expanded = new Set<string>(removedStepIds);

  if (expanded.has('facility-power')) {
    if (!nextSet.has('large-ups')) {
      expanded.add('large-ups');
    }
    if (!nextSet.has('power-distribution')) {
      expanded.add('power-distribution');
    }
  }

  return [...expanded].filter(isOptionalRequestFormStepId);
}

export function getFormFieldKeysForRemovedSteps(
  removedStepIds: readonly string[],
  nextStoredStepIds?: string[] | null,
  stage?: string | null,
): string[] {
  const expanded = expandRemovedStepIdsForDataClear(
    removedStepIds,
    nextStoredStepIds,
  );
  const keys = new Set<string>();
  for (const stepId of expanded) {
    if (!isOptionalRequestFormStepId(stepId)) {
      continue;
    }
    for (const key of REQUEST_STEP_FORM_FIELD_KEYS[stepId]) {
      keys.add(key);
    }
  }

  if (
    !shouldRetainPowerAdditionalInformation(
      resolveEnabledStepIds(nextStoredStepIds),
      stage,
    ) &&
    expanded.some(
      id =>
        id === 'facility-power' ||
        id === 'large-ups' ||
        id === 'power-distribution',
    )
  ) {
    keys.add(POWER_ADDITIONAL_INFORMATION_FIELD);
  }

  return [...keys];
}

export function stripFormFieldsForRemovedSteps<
  T extends Record<string, unknown>,
>(
  data: T,
  removedStepIds: readonly string[],
  nextStoredStepIds?: string[] | null,
  stage?: string | null,
): T {
  const keys = getFormFieldKeysForRemovedSteps(
    removedStepIds,
    nextStoredStepIds,
    stage,
  );
  if (keys.length === 0) {
    return data;
  }

  const next = { ...data };
  for (const key of keys) {
    delete next[key];
  }

  const expanded = expandRemovedStepIdsForDataClear(
    removedStepIds,
    nextStoredStepIds,
  );
  if (expanded.includes('rack-power')) {
    delete next.rackConfigurations;
  }
  if (expanded.includes('large-ups')) {
    delete next.largeUpsConfigurations;
  }
  if (expanded.includes('power-distribution')) {
    delete next.powerDistributionConfigurations;
  }
  if (expanded.includes('small-ups')) {
    delete next.smallUpsConfigurations;
    delete next.smallUpsInstances;
  }
  if (expanded.includes('cooling-readiness')) {
    delete next.coolingSystemConfigurations;
    delete next.coolingInstances;
  }
  if (expanded.includes('liquid-cooling')) {
    delete next.liquidCoolingCduConfigurations;
    delete next.liquidCoolingInstances;
  }

  return next;
}

export function pruneDirtyFieldsForRemovedSteps(
  dirtyFields: Set<string>,
  removedStepIds: readonly string[],
  nextStoredStepIds?: string[] | null,
  stage?: string | null,
): Set<string> {
  const keys = new Set(
    getFormFieldKeysForRemovedSteps(removedStepIds, nextStoredStepIds, stage),
  );
  // The Racks engine session dirty-tracks the `rackConfigurations` array key
  // (#997 Story 6.3) — prune it with the step, or scope removal leaves a
  // phantom dirty flag (discard prompts over nothing, draft snapshots that
  // never clear). Mirrors the editedData strip in
  // `stripFormFieldsForRemovedSteps` above.
  const expanded = expandRemovedStepIdsForDataClear(
    removedStepIds,
    nextStoredStepIds,
  );
  if (expanded.includes('rack-power')) {
    keys.add('rackConfigurations');
  }
  // Small UPS engine session dirty-tracks the `smallUpsConfigurations` array key
  // (#997 Story 6.4) — same phantom-dirty-flag prune as racks.
  if (expanded.includes('small-ups')) {
    keys.add('smallUpsConfigurations');
  }
  // Large UPS engine session dirty-tracks the `largeUpsConfigurations` array key
  // (#997 Story 6.5) — same phantom-dirty-flag prune as racks/small UPS.
  if (expanded.includes('large-ups')) {
    keys.add('largeUpsConfigurations');
  }
  // Cooling engine session dirty-tracks the `coolingSystemConfigurations` array
  // key (#997 Story 6.6) — same phantom-dirty-flag prune as racks/UPS.
  if (expanded.includes('cooling-readiness')) {
    keys.add('coolingSystemConfigurations');
  }
  // Power Distribution engine session dirty-tracks the
  // `powerDistributionConfigurations` array key (#997 Story 6.7) — same
  // phantom-dirty-flag prune as racks/UPS/cooling.
  if (expanded.includes('power-distribution')) {
    keys.add('powerDistributionConfigurations');
  }
  // Liquid Cooling engine session dirty-tracks the
  // `liquidCoolingCduConfigurations` array key (#997 Story 6.8) — same
  // phantom-dirty-flag prune as racks/UPS/cooling/power distribution.
  if (expanded.includes('liquid-cooling')) {
    keys.add('liquidCoolingCduConfigurations');
  }
  if (keys.size === 0) {
    return dirtyFields;
  }

  const next = new Set(dirtyFields);
  for (const key of keys) {
    next.delete(key);
  }
  return next;
}
